import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Stop, Clock } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const hms = (ms) => {
  const t = Math.max(0, Math.floor(ms / 1000));
  const h = String(Math.floor(t / 3600)).padStart(2, "0");
  const m = String(Math.floor((t % 3600) / 60)).padStart(2, "0");
  const s = String(t % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
};

const pausedMs = (session) =>
  (session.pauses || []).reduce((sum, p) => {
    const start = new Date(p.start).getTime();
    const end = p.end ? new Date(p.end).getTime() : Date.now();
    return sum + Math.max(0, end - start);
  }, 0);

export default function RunningTimers() {
  const qc = useQueryClient();
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState("");

  const { data: sessions = [] } = useQuery({
    queryKey: ["work-sessions"],
    queryFn: async () => (await api.get("/work/sessions")).data,
    refetchInterval: 30000,
  });
  const { data: jobs = [] } = useQuery({ queryKey: ["entity", "jobs"], queryFn: async () => (await api.get("/jobs")).data });
  const { data: machines = [] } = useQuery({ queryKey: ["entity", "machines"], queryFn: async () => (await api.get("/machines")).data });
  const { data: operations = [] } = useQuery({ queryKey: ["entity", "operations"], queryFn: async () => (await api.get("/operations")).data });

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const live = sessions.filter((s) => ["Running", "Paused"].includes(s.status));
  if (live.length === 0) return null;

  const endSession = async (s) => {
    setBusy(s.id);
    try {
      const { data } = await api.post(`/work/sessions/${s.id}/end`);
      toast.success(`${s.code} ended — ${data.actual_hours ?? 0} h saved to the production log`);
      qc.invalidateQueries({ queryKey: ["work-sessions"] });
      qc.invalidateQueries({ queryKey: ["work-assignments"] });
      qc.invalidateQueries({ queryKey: ["entity", "production"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const endAll = async (force) => {
    setBusy("all");
    try {
      const { data } = await api.post("/work/sessions/end-all", { force });
      if (data.count === 0) {
        toast.info("No timers are past the shift end yet — use End all anyway if you want to close them now");
      } else {
        toast.success(`${data.count} timer${data.count === 1 ? "" : "s"} closed and saved`);
      }
      qc.invalidateQueries({ queryKey: ["work-sessions"] });
      qc.invalidateQueries({ queryKey: ["work-assignments"] });
      qc.invalidateQueries({ queryKey: ["entity", "production"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <Card className="mb-6 border-emerald-200 bg-emerald-50/40 shadow-none" data-testid="running-timers">
      <div className="flex flex-wrap items-center gap-2 px-5 py-3.5 border-b border-emerald-100">
        <Clock className="h-5 w-5 text-emerald-600" weight="fill" />
        <h3 className="font-semibold text-emerald-900">Live Timers — {live.length} running now</h3>
        <span className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" className="h-7 border-slate-300" disabled={busy === "all"}
            onClick={() => endAll(false)} data-testid="end-all-past-shift">
            End timers past shift
          </Button>
          <Button size="sm" variant="outline" className="h-7 border-red-200 text-red-700 hover:bg-red-50"
            disabled={busy === "all"} onClick={() => endAll(true)} data-testid="end-all-force">
            End all now
          </Button>
        </span>
      </div>
      <ul className="divide-y divide-emerald-100">
        {live.map((s) => {
          const started = new Date(s.start_time).getTime();
          const gross = now - started;
          const net = gross - pausedMs(s);
          return (
            <li key={s.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-3"
              data-testid={`live-timer-${s.code}`}>
              <span className="min-w-0 text-sm">
                <span className="font-mono text-xs text-slate-500">{s.code}</span>{" "}
                <span className="font-medium text-slate-900">{s.operator_name}</span>
                <Badge variant="outline" className={`ml-2 ${s.status === "Paused" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"}`}>
                  {s.status}
                </Badge>
                <span className="block text-xs text-slate-600 mt-0.5">
                  {[jobs.find((j) => j.id === s.job_id)?.code,
                    machines.find((m) => m.id === s.machine_id)?.machine_name,
                    operations.find((o) => o.id === s.operation_id)?.operation_name]
                    .filter(Boolean).join(" · ") || "—"}
                  {s.started_on_behalf && s.started_by ? ` · started by ${s.started_by}` : ""}
                </span>
              </span>
              <span className="flex items-center gap-3 shrink-0">
                <span className="text-right">
                  <span className="block font-mono text-lg font-bold text-slate-900 tabular-nums"
                    data-testid={`live-elapsed-${s.code}`}>{hms(net)}</span>
                  <span className="block text-[11px] text-slate-500">
                    since {new Date(s.start_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                    {(s.pauses || []).length ? ` · ${(s.pauses || []).length} break(s)` : ""}
                  </span>
                </span>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={busy === s.id}
                  onClick={() => endSession(s)} data-testid={`live-end-${s.code}`}>
                  <Stop className="h-4 w-4 mr-1.5" weight="fill" /> {busy === s.id ? "Ending…" : "End & save"}
                </Button>
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
